/**
 * Button: the one button for the dashboard.
 *
 *   <Button onClick={save}>Save</Button>                                  primary, md (36px), type="button"
 *   <Button variant="secondary" size="sm" leftIcon="mdi:code-tags">Code</Button>
 *   <Button variant="danger" loading={deleting}>Delete key</Button>       spinner, disabled, aria-busy
 *   <Button type="submit" loading={submitting}>Start crawl</Button>
 *   <Button href="/dashboard/billing" variant="secondary">Upgrade</Button> renders a Next <Link>
 *
 * variant  primary | secondary | ghost | danger | danger-secondary (outlined red, for "Cancel plan" style actions)
 * size     sm 32px | md 36px | lg 40px. On touch screens sm and md grow to 40px.
 * leftIcon / rightIcon take an Iconify name ("mdi:plus") or any node.
 * loadingText replaces the label while loading ("Saving..."); otherwise the label stays and a spinner is added.
 * fullWidth stretches to the container. type defaults to "button", so a Button inside a form never submits by
 * accident; pass type="submit" on purpose.
 */
import { forwardRef, ReactNode, ButtonHTMLAttributes } from "react";
import Link from "next/link";
import { Icon } from "@iconify/react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-secondary";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-white hover:bg-primary-hover active:bg-primary-hover shadow-card disabled:bg-primary/50",
  secondary:
    "bg-surface text-fg border border-line-strong hover:bg-surface-muted dark:bg-white/5 dark:hover:bg-white/10 shadow-card",
  ghost: "text-fg-secondary hover:bg-black/5 hover:text-fg dark:hover:bg-white/10",
  danger: "bg-danger text-white hover:bg-danger-hover shadow-card disabled:bg-danger/50",
  "danger-secondary":
    "bg-surface text-danger-text border border-line-strong hover:bg-danger-subtle hover:border-danger/40 dark:bg-white/5",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-13 gap-1.5 pointer-coarse:h-10",
  md: "h-9 px-3.5 text-sm gap-2 pointer-coarse:h-10",
  lg: "h-10 px-4 text-sm gap-2",
};

const ICON_SIZES: Record<ButtonSize, string> = { sm: "text-sm", md: "text-base", lg: "text-lg" };

export function buttonClasses({
  variant = "primary",
  size = "md",
  fullWidth = false,
  className = "",
}: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean; className?: string } = {}) {
  return [
    "inline-flex items-center justify-center whitespace-nowrap rounded-control font-medium select-none",
    "transition-colors duration-150 cursor-pointer",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
    "disabled:cursor-not-allowed disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60",
    VARIANTS[variant],
    SIZES[size],
    fullWidth ? "w-full min-w-0 flex-1" : "shrink-0",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

export function ButtonSpinner({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-[1em] w-[1em] shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent ${className}`}
    />
  );
}

const renderIcon = (icon: ReactNode | string | undefined, size: ButtonSize) =>
  typeof icon === "string" ? <Icon icon={icon} className={`${ICON_SIZES[size]} shrink-0`} aria-hidden="true" /> : icon;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: ReactNode;
  leftIcon?: ReactNode | string;
  rightIcon?: ReactNode | string;
  fullWidth?: boolean;
  /** Render as a link instead. Internal paths use Next's Link; set external for a new tab. */
  href?: string;
  external?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    loadingText,
    leftIcon,
    rightIcon,
    fullWidth = false,
    href,
    external = false,
    className = "",
    type = "button",
    disabled,
    children,
    ...rest
  },
  ref
) {
  const classes = buttonClasses({ variant, size, fullWidth, className });
  // The label sits in its own <span>. Browser translators (Chrome/Google Translate, Edge) replace bare text nodes
  // with <font> elements; when the spinner was then inserted before the bare "Log in" text, React's insertBefore
  // threw and the root error boundary blanked the page. Reproduced on /login and /forgot-password, 2026-11-03.
  // display: contents keeps the label's children laid out as before, as direct flex items of the button.
  const content = (
    <>
      {loading ? <ButtonSpinner /> : renderIcon(leftIcon, size)}
      {children !== undefined && children !== null && children !== false ? (
        <span className="contents">{loading && loadingText ? loadingText : children}</span>
      ) : null}
      {!loading && renderIcon(rightIcon, size)}
    </>
  );

  if (href && !disabled) {
    const linkProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
    return (
      <Link
        href={href}
        className={classes}
        {...linkProps}
        onClick={rest.onClick as unknown as React.MouseEventHandler<HTMLAnchorElement>}
        aria-label={rest["aria-label"]}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
